import { formatSecondsAsTimer } from "@vericus/cadmus-common";
import { RecordCircleIcon } from "@vericus/cadmus-icons";
import { colors, Text } from "@vericus/cadmus-ui";

import { differenceInSeconds } from "date-fns";

import type { UseWebcamCaptureReturn } from "@/components/webcam-capture";
import { useAppSelector } from "@/data/hooks";
import type { CountdownMessage } from "@/features/timeline/use-countdown/countdown-message";
import { getCurrentDate } from "@/utils/datetime";

import {
  selectResponseEndDate,
  selectResponseEndDatePassed,
  selectSocraticDialogueState,
} from "../../socratic-slice";
import * as styles from "./socratic-timers.css";

interface Props {
  webcam: UseWebcamCaptureReturn;
  preparationCountdown: CountdownMessage | null;
}

export function SocraticTimers(props: Props) {
  const { webcam, preparationCountdown } = props;

  const responseEndDate = useAppSelector(selectResponseEndDate);
  const responseEndDatePassed = useAppSelector(selectResponseEndDatePassed);

  const {
    isRecording,
    responseTimeSeconds: responseTimeLimit,
    recordingComplete,
  } = useAppSelector(selectSocraticDialogueState);

  const showPreparationCountdown = preparationCountdown !== null;

  const recordingFailed = webcam.error != null;

  const showRecordingTimer =
    isRecording && !recordingFailed && responseEndDatePassed === false;

  return (
    <>
      {isRecording &&
        (recordingFailed ? (
          <RecordingFailedIndicator />
        ) : (
          <RecordingIndicator />
        ))}
      {showPreparationCountdown && (
        <PreparationCountdown message={preparationCountdown.message} />
      )}

      {showRecordingTimer && responseEndDate != null && (
        <RecordingTimer
          responseEndDate={responseEndDate}
          responseTimeLimit={responseTimeLimit ?? webcam.maxDurationSeconds}
        />
      )}

      {recordingComplete && <ReadyToSubmitIndicator />}
    </>
  );
}

function RecordingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <RecordCircleIcon
        size={24}
        color={colors.poppy500}
        label=""
        className={styles.recordingDot}
      />
      <Text kind="bodyLine">Recording now</Text>
    </div>
  );
}

function RecordingFailedIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <RecordCircleIcon size={24} color={colors.grey500} label="" />
      <Text kind="bodyLine">Recording failed</Text>
    </div>
  );
}

function PreparationCountdown(props: { message: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <RecordCircleIcon size={24} color={colors.poppy500} label="" />
      <Text kind="bodyLine">Recording will start in {props.message}</Text>
    </div>
  );
}

function ReadyToSubmitIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <RecordCircleIcon size={24} color={colors.poppy500} label="" />
      <Text kind="bodyLine">Ready to submit</Text>
    </div>
  );
}

/**
 * Counts against the response deadline rather than the recorder's own clock.
 * After a resume the recorder restarts at zero, and the seconds spent bringing
 * the camera back belong to the student's window too — reading the recorder
 * would show them time they no longer have.
 */
function RecordingTimer(props: {
  responseEndDate: string;
  responseTimeLimit: number;
}) {
  const secondsRemaining = Math.max(
    0,
    differenceInSeconds(new Date(props.responseEndDate), getCurrentDate())
  );
  const responseDuration = Math.max(
    0,
    props.responseTimeLimit - secondsRemaining
  );

  return (
    <div style={{ display: "flex" }}>
      <Text kind="headingTwo">{formatSecondsAsTimer(responseDuration)}</Text>
      <Text kind="headingTwo" color="grey300">
        /{formatSecondsAsTimer(props.responseTimeLimit)}
      </Text>
    </div>
  );
}
